import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, Loader2, MessageCircle, Paperclip, Mic, MicOff, X, FileText, Image } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/auth.store';
import { getInitials } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

interface Mensaje {
  id: string;
  conversacionKey: string;
  remitenteId: string;
  destinatarioId: string;
  tipo: 'texto' | 'imagen' | 'archivo';
  contenido?: string;
  adjuntoUrl?: string;
  adjuntoNombre?: string;
  adjuntoMime?: string;
  leido: boolean;
  leidoAt?: string;
  createdAt: string;
  remitente?: { id: string; nombre: string; usuario?: { fotoUrl?: string } };
}

interface MedicoInfo {
  id: string;
  nombre: string;
  especialidad?: { nombre: string };
  usuario?: { fotoUrl?: string };
}

function formatMsgTime(dateStr: string) {
  return format(new Date(dateStr), 'HH:mm');
}

function formatDayLabel(dateStr: string) {
  const d = new Date(dateStr);
  if (isToday(d)) return 'Hoy';
  if (isYesterday(d)) return 'Ayer';
  return format(d, "d 'de' MMMM", { locale: es });
}

function groupByDay(msgs: Mensaje[]) {
  const groups: { day: string; msgs: Mensaje[] }[] = [];
  for (const msg of msgs) {
    const day = format(new Date(msg.createdAt), 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last?.day === day) last.msgs.push(msg);
    else groups.push({ day, msgs: [msg] });
  }
  return groups;
}

function formatTimer(s: number) {
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

interface Props {
  medicoId: string;
  onBack: () => void;
}

export function ChatConversacion({ medicoId, onBack }: Props) {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [typing, setTyping] = useState(false);
  const typingTimeout = useRef<ReturnType<typeof setTimeout>>();
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const socket = getSocket();

  // ── Audio recording ─────────────────────────────────────────────────────
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [subiendoAudio, setSubiendoAudio] = useState(false);

  useEffect(() => {
    if (grabando) {
      timerRef.current = setInterval(() => setSegundos(s => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      setSegundos(0);
    }
    return () => clearInterval(timerRef.current);
  }, [grabando]);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: medicoInfo } = useQuery<MedicoInfo>({
    queryKey: ['medico-chat-info', medicoId],
    queryFn: () => api.get(`/api/red/medico/${medicoId}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const { data: mensajes = [], isLoading } = useQuery<Mensaje[]>({
    queryKey: ['chat-historial', medicoId],
    queryFn: () => api.get(`/api/chat/${medicoId}`).then(r => r.data),
    staleTime: 0,
  });

  // Scroll al fondo
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes, typing]);

  // Marcar como leídos al abrir
  useEffect(() => {
    socket.emit('chat:marcar-leido', { medicoRemitenteId: medicoId });
    qc.invalidateQueries({ queryKey: ['chat-conversaciones'] });
    qc.invalidateQueries({ queryKey: ['notificaciones'] });
  }, [medicoId, socket, qc]);

  // Mensajes y typing en tiempo real
  useEffect(() => {
    function onMensaje(msg: Mensaje) {
      const involucra = msg.remitenteId === medicoId || msg.destinatarioId === medicoId;
      if (!involucra) return;
      qc.setQueryData<Mensaje[]>(['chat-historial', medicoId], prev =>
        prev ? [...prev.filter(m => m.id !== msg.id), msg] : [msg],
      );
      if (msg.destinatarioId === usuario?.id) {
        socket.emit('chat:marcar-leido', { medicoRemitenteId: medicoId });
      }
    }

    function onTyping({ medicoId: from, escribiendo }: { medicoId: string; escribiendo: boolean }) {
      if (from === medicoId) setTyping(escribiendo);
    }

    function onLeido({ porMedicoId }: { porMedicoId: string }) {
      if (porMedicoId !== medicoId) return;
      qc.setQueryData<Mensaje[]>(['chat-historial', medicoId], prev =>
        prev?.map(m => m.remitenteId !== medicoId ? { ...m, leido: true } : m) ?? [],
      );
    }

    socket.on('chat:mensaje', onMensaje);
    socket.on('chat:typing', onTyping);
    socket.on('chat:leido', onLeido);
    return () => {
      socket.off('chat:mensaje', onMensaje);
      socket.off('chat:typing', onTyping);
      socket.off('chat:leido', onLeido);
    };
  }, [medicoId, socket, qc, usuario?.id]);

  // ── Envíos ────────────────────────────────────────────────────────────────

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTexto(e.target.value);
    socket.emit('chat:typing', { destinatarioId: medicoId, escribiendo: true });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit('chat:typing', { destinatarioId: medicoId, escribiendo: false });
    }, 1500);
  }, [medicoId, socket]);

  async function enviarTexto() {
    const t = texto.trim();
    if (!t || enviando) return;
    setTexto('');
    setEnviando(true);
    socket.emit('chat:typing', { destinatarioId: medicoId, escribiendo: false });
    socket.emit('chat:enviar', { destinatarioId: medicoId, tipo: 'texto', contenido: t });
    setEnviando(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarTexto(); }
  }

  async function uploadAndSend(blob: Blob, nombre: string, mime: string) {
    const fd = new FormData();
    fd.append('file', blob, nombre);
    try {
      const { data } = await api.post(
        `/api/storage/upload?entityId=${medicoId}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const tipo = mime.startsWith('image/') ? 'imagen' : 'archivo';
      socket.emit('chat:enviar', {
        destinatarioId: medicoId,
        tipo,
        adjuntoUrl: data.url,
        adjuntoNombre: nombre,
        adjuntoMime: mime,
      });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir el archivo'));
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadAndSend(file, file.name, file.type);
    e.target.value = '';
  }

  async function iniciarGrabacion() {
    if (grabando) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 200) {
          toast.error('El audio es demasiado corto. Mantén presionado para grabar.');
          return;
        }
        setSubiendoAudio(true);
        await uploadAndSend(blob, `audio_${Date.now()}.webm`, 'audio/webm');
        setSubiendoAudio(false);
      };
      recorder.start(250);
      mediaRecorderRef.current = recorder;
      setGrabando(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo acceder al micrófono'));
    }
  }

  function detenerGrabacion() {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.requestData(); // vacía el buffer pendiente antes de stop
      rec.stop();
    }
    setGrabando(false);
  }

  function cancelarGrabacion() {
    const rec = mediaRecorderRef.current;
    if (rec) {
      rec.ondataavailable = null;
      rec.onstop = null;
      try { rec.stop(); } catch {}
      try { (rec as any).stream?.getTracks().forEach((t: MediaStreamTrack) => t.stop()); } catch {}
    }
    setGrabando(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const groups = groupByDay(mensajes);
  const fotoOtro = medicoInfo?.usuario?.fotoUrl ?? null;

  return (
    <div className="flex flex-col h-full">
      {/* Mensajes */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 size={22} className="animate-spin text-gray-300" />
          </div>
        ) : mensajes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle size={36} className="text-gray-200 mb-3" />
            <p className="text-sm font-medium text-text-primary">Inicia la conversación</p>
            <p className="text-xs text-text-secondary mt-1">
              Envía un mensaje a Dr. {medicoInfo?.nombre}
            </p>
          </div>
        ) : (
          groups.map(({ day, msgs }) => (
            <div key={day}>
              {/* Separador de día */}
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[10px] text-gray-400 font-medium px-1">
                  {formatDayLabel(msgs[0].createdAt)}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {msgs.map((msg, i) => {
                const isMe = msg.remitenteId !== medicoId;
                // Mostrar avatar solo en el último mensaje consecutivo del mismo remitente
                const nextMsg = msgs[i + 1];
                const isLastOfGroup = !nextMsg || nextMsg.remitenteId !== msg.remitenteId;

                return (
                  <div
                    key={msg.id}
                    className={cn('flex items-end gap-1.5 mb-0.5', isMe ? 'justify-end' : 'justify-start')}
                  >
                    {/* Avatar del otro médico (izquierda) */}
                    {!isMe && (
                      <div className="w-6 h-6 flex-shrink-0 mb-0.5">
                        {isLastOfGroup && (
                          <div className="w-6 h-6 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center text-primary-600 text-[9px] font-bold">
                            {fotoOtro
                              ? <img src={fotoOtro} alt="" className="w-full h-full object-cover" />
                              : getInitials(medicoInfo?.nombre ?? 'M')
                            }
                          </div>
                        )}
                      </div>
                    )}

                    {/* Burbuja */}
                    <div
                      className={cn(
                        'max-w-[72%] px-3 py-2 rounded-2xl text-sm',
                        isMe
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-text-primary rounded-bl-sm',
                      )}
                    >
                      <MensajeBurbuja msg={msg} isMe={isMe} />
                      {isLastOfGroup && (
                        <p className={cn(
                          'text-[10px] mt-1',
                          isMe ? 'text-right text-blue-200' : 'text-left text-gray-400',
                        )}>
                          {formatMsgTime(msg.createdAt)}
                          {isMe && (msg.leido ? ' ✓✓' : ' ✓')}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Typing */}
        {typing && (
          <div className="flex items-end gap-1.5 justify-start mb-0.5">
            <div className="w-6 h-6 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center text-primary-600 text-[9px] font-bold flex-shrink-0">
              {fotoOtro
                ? <img src={fotoOtro} alt="" className="w-full h-full object-cover" />
                : getInitials(medicoInfo?.nombre ?? 'M')
              }
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2.5 flex gap-1 items-center">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Input area ────────────────────────────────────────────────────── */}
      <div className="px-3 py-2.5 border-t border-gray-100 flex-shrink-0">
        {grabando ? (
          /* UI de grabación */
          <div className="flex items-center gap-2">
            <button
              onClick={cancelarGrabacion}
              className="p-2 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors flex-shrink-0"
              title="Cancelar"
            >
              <X size={15} />
            </button>
            <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-red-600 text-sm font-mono font-medium">{formatTimer(segundos)}</span>
              <span className="text-red-400 text-xs">Grabando...</span>
            </div>
            <button
              onClick={detenerGrabacion}
              disabled={subiendoAudio}
              className="p-2 rounded-xl bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors flex-shrink-0"
              title="Enviar audio"
            >
              {subiendoAudio
                ? <Loader2 size={15} className="animate-spin" />
                : <Send size={15} />
              }
            </button>
          </div>
        ) : (
          /* UI normal */
          <div className="flex items-center gap-1.5">
            {/* Adjunto */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Adjuntar archivo"
            >
              <Paperclip size={16} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFile}
            />

            {/* Texto */}
            <input
              type="text"
              value={texto}
              onChange={handleInput}
              onKeyDown={onKeyDown}
              placeholder="Escribe un mensaje..."
              className="flex-1 h-9 px-3 rounded-xl bg-gray-50 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all border border-gray-100"
            />

            {/* Micrófono o enviar */}
            {texto.trim() ? (
              <button
                onClick={enviarTexto}
                disabled={enviando}
                className="p-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors flex-shrink-0"
              >
                {enviando
                  ? <Loader2 size={15} className="animate-spin" />
                  : <Send size={15} />
                }
              </button>
            ) : (
              <button
                onClick={iniciarGrabacion}
                className="p-2 rounded-xl bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors flex-shrink-0"
                title="Grabar audio"
              >
                <Mic size={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Render del contenido de cada burbuja ────────────────────────────────────

function MensajeBurbuja({ msg, isMe }: { msg: Mensaje; isMe: boolean }) {
  if (msg.tipo === 'texto') {
    return <p className="leading-relaxed text-sm break-words">{msg.contenido}</p>;
  }

  if (msg.adjuntoMime?.startsWith('audio/')) {
    return (
      <audio
        controls
        src={msg.adjuntoUrl}
        className="max-w-[200px] h-8"
        style={{ colorScheme: isMe ? 'dark' : 'light' }}
      />
    );
  }

  if (msg.tipo === 'imagen' || msg.adjuntoMime?.startsWith('image/')) {
    return (
      <a href={msg.adjuntoUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={msg.adjuntoUrl}
          alt={msg.adjuntoNombre ?? 'imagen'}
          className="max-w-full rounded-lg max-h-48 object-cover"
        />
      </a>
    );
  }

  // Archivo genérico
  return (
    <a
      href={msg.adjuntoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 text-xs underline underline-offset-2',
        isMe ? 'text-blue-100' : 'text-blue-600',
      )}
    >
      {msg.adjuntoMime?.startsWith('image/') ? <Image size={13} /> : <FileText size={13} />}
      <span className="truncate max-w-[140px]">{msg.adjuntoNombre ?? 'Archivo'}</span>
    </a>
  );
}
